"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, Clock, ShieldCheck, PhoneCall, Mail, CircleCheck as CheckCircle2, CalendarCheck, Loader as Loader2, Send, CircleAlert as AlertCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const supportHighlights = [{
  title: "Rapid Response & Dispatch",
  description: "Certified engineers deployed across Ireland and the UK for emergency repair."
}, {
  title: "Preventative Maintenance",
  description: "Scheduled calibration and qualification to prevent unexpected instrument downtime."
}, {
  title: "Compliance Documentation",
  description: "Full audit-trail compliant service reports, IQ/OQ validation, and calibration certificates."
}];
function MaintenanceRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-card rounded-xl border border-border shadow-md">
        <div className="inline-flex size-14 items-center justify-center rounded-full bg-secondary text-primary mb-4">
          <CheckCircle2 className="size-7 text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-card-foreground mb-2">
          Request Received
        </h3>
        <p className="text-muted-foreground text-sm max-w-sm mb-6">
          {successMessage || "Thank you for reaching out. Our engineering coordination team will review your schedule details and contact you promptly."}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-secondary">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Card className="border-border shadow-md bg-card">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5">
            Service Request Form
          </Badge>
        </div>
        <CardTitle className="text-2xl font-semibold text-card-foreground">
          Contact Us - Antech Solutions
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Fill in your details below to schedule routine maintenance, request calibration, or book emergency repairs.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField control={control} name="name" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground">Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" className="bg-background" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="work_email" rules={{
            required: "Work email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground">Work Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" className="bg-background" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField control={control} name="company_name" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground">Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Corp" className="bg-background" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone_number" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground">Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+44 20 1234 5678" className="bg-background" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="message" rules={{
          required: "Message is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Message *</FormLabel>
                <FormControl>
                  <Textarea placeholder="Describe your instrument model, service requirement, preferred schedule, or urgent fault..." rows={4} className="bg-background resize-y" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
              <AlertCircle className="size-4 shrink-0" />
              <span>{submitError}</span>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 text-base font-medium shadow-xs">
            {isSubmitting ? <>
                <Loader2 className="animate-spin size-4 mr-2" />
                Submitting Request...
              </> : <>
                <Send className="size-4 mr-2" />
                Schedule Service Now
              </>}
          </Button>

          <p className="text-xs text-center text-muted-foreground pt-1">
            We respect your privacy. Standard response time is under 4 business hours.
          </p>
        </Form>
      </CardContent>
    </Card>;
}
export default function MaintenanceCTA() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="maintenanceschedulecta" data-nav="light" className="bg-background py-16 sm:py-24 border-t border-border">
      <div className="page-container">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {/* Left Column: Context, Trust & Direct Help */}
          <motion.div variants={itemVariants} className="lg:col-span-6 flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
                <CalendarCheck className="size-3.5 text-primary" />
                Laboratory Maintenance &amp; Repairs
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mb-4">
                Keep Your Analytical Systems Qualified and Operational
              </h2>

              <p className="text-base text-muted-foreground leading-relaxed mb-8">
                Minimize instrument downtime and guarantee strict regulatory compliance. Our factory-trained field service team provides responsive diagnostic troubleshooting, routine maintenance plans, and full qualification support throughout Ireland and the UK.
              </p>

              {/* Highlights List */}
              <div className="space-y-4 mb-8">
                {supportHighlights.map((item, i) => <div key={item.title} data-index={i} className="flex items-start gap-3 p-3.5 rounded-lg bg-secondary/50 border border-border/70">
                    <div className="p-1.5 rounded-md bg-background text-primary shrink-0 shadow-2xs">
                      {i === 0 && <Clock className="size-4" />}
                      {i === 1 && <Wrench className="size-4" />}
                      {i === 2 && <ShieldCheck className="size-4" />}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {item.title}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Direct Support Card */}
            <div className="p-5 rounded-xl bg-secondary/80 border border-border">
              <h3 className="text-sm font-semibold text-secondary-foreground mb-3">
                Need urgent breakdown assistance?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <a href="tel:+442012345678" className="flex items-center gap-2.5 p-2.5 rounded-md bg-background text-foreground hover:text-primary transition-colors border border-border/50 shadow-2xs">
                  <PhoneCall className="size-4 text-primary" />
                  <span className="font-medium text-xs sm:text-sm truncate">Emergency Helpline</span>
                </a>
                <a href="mailto:support@antechsolutions.com" className="flex items-center gap-2.5 p-2.5 rounded-md bg-background text-foreground hover:text-primary transition-colors border border-border/50 shadow-2xs">
                  <Mail className="size-4 text-primary" />
                  <span className="font-medium text-xs sm:text-sm truncate">support@antech.com</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Form Provider Container */}
          <motion.div variants={itemVariants} className="lg:col-span-6">
            <FormProvider formId="contact_form" sectionName="maintenanceschedulecta" defaultValues={{
            name: "",
            work_email: "",
            company_name: "",
            phone_number: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            work_email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Schedule Service Now" successMessage="Thank you. Your service and maintenance request has been submitted to our technical engineering team." errorMessage="Failed to submit your request. Please check your details and try again.">
              <MaintenanceRequestForm />
            </FormProvider>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}